import safetyIcon from "@assets/images/Frame-1618869423.svg";
import billingIcon from "@assets/images/Frame-1618869424.svg";
import supplyIcon from "@assets/images/Frame-1618869425.svg";
import supportIcon from "@assets/images/Frame-1618869426.svg";
import { Reveal } from "@components/ui";

/* ─────────────────────────────────────────────────────────
   Sub-components
   ───────────────────────────────────────────────────────── */

interface FeatureCardProps {
  icon: string;
  title: string;
  description: string;
  index: number;
}

function FeatureCard({ icon, title, description, index }: FeatureCardProps) {
  const watermark = String(index + 1).padStart(2, "0");

  return (
    <div className="group relative w-full rounded-[12px] md:rounded-[24px] bg-white flex flex-col items-start p-5 md:pt-8 md:px-8 md:pb-6 lg:pt-10 lg:px-10 lg:pb-8 overflow-hidden hover:shadow-[0_4px_20px_rgba(0,0,0,0.08)] transition-all duration-300 border border-transparent hover:border-[#e8e9ed]">
      {/* Watermark number */}
      <span className="absolute top-4 right-5 md:top-6 md:right-7 lg:top-8 lg:right-9 text-[48px] md:text-[64px] lg:text-[80px] font-bold text-[#1c1c1c]/[0.04] leading-none font-albert-sans select-none pointer-events-none">
        {watermark}
      </span>

      <div className="self-stretch flex-1 flex flex-col justify-between gap-6 lg:gap-8">
        <div className="self-stretch flex flex-col items-start gap-5 lg:gap-7">
          {/* Tinted icon container */}
          <div className="w-12 h-12 md:w-14 md:h-14 rounded-[14px] bg-[#f24d2b]/[0.08] group-hover:bg-[#f24d2b]/[0.15] flex items-center justify-center transition-colors duration-300">
            <img
              src={icon}
              alt=""
              className="w-6 h-6 md:w-7 md:h-7"
              loading="lazy"
            />
          </div>
          <h2 className="self-stretch text-sm md:text-lg lg:text-[32px] tracking-[-0.04em] font-semibold text-[#1c1c1c] font-albert-sans">
            {title}
          </h2>
        </div>
        <p className="self-stretch text-[14px] md:text-base lg:text-[18px] text-[rgba(10,14,46,0.75)] font-albert-sans leading-[1.6]">
          {description}
        </p>
      </div>
    </div>
  );
}

const features = [
  {
    icon: safetyIcon,
    title: "Certified Safety Standards",
    description:
      "Every installation and inspection follows UAE Civil Defense and ESMA regulations, ensuring peace of mind and operational safety.",
  },
  {
    icon: billingIcon,
    title: "Smart Billing Easy Payments",
    description:
      "Instantly pay, view, and track your gas usage through our secure online portal or mobile app.",
  },
  {
    icon: supplyIcon,
    title: "Reliable Supply Network",
    description:
      "Whether residential or commercial, GASIS guarantees consistent gas flow backed by proactive maintenance and monitoring.",
  },
  {
    icon: supportIcon,
    title: "24/7 Customer Support",
    description:
      "Every installation and inspection follows UAE Civil Defense and ESMA regulations, ensuring peace of mind and operational safety.",
  },
];

/* ─────────────────────────────────────────────────────────
   Main component
   ───────────────────────────────────────────────────────── */

export function WhyChooseSection() {
  return (
    <section className="relative w-full bg-[#f6f6f9]">
      <div className="py-10 md:py-14 lg:py-[100px] px-5 md:px-10 lg:px-[180px]">
        <div className="w-full max-w-[1368px] mx-auto flex flex-col gap-5">
          {/* ── CTA Card ── */}
          <Reveal direction="up">
            <div className="w-full">
              <div className="rounded-[12px] md:rounded-[24px] bg-[#f34d2b] flex flex-col items-start justify-center py-10 lg:py-20 px-6 sm:px-8 lg:px-10 gap-4 lg:gap-[23.5px]">
                <h2 className="text-[24px] md:text-[40px] lg:text-[48px] tracking-[-0.04em] font-bold text-white font-albert-sans">
                  Why Choose GASIS
                </h2>
                <p className="w-full max-w-[580px] text-[14px] md:text-base lg:text-[18px] text-white font-albert-sans leading-[1.5]">
                  At GIS, we're dedicated to delivering reliable and hassle-free
                  LPG solutions. With a focus on safety and customer satisfaction,
                  we offer a range of services, from supply and maintenance to
                  innovative online platforms.
                </p>
                <button className="group/btn flex items-center gap-2.5 text-xs md:text-base lg:text-[20px] tracking-[-0.04em] font-normal text-white font-albert-sans hover:opacity-80 transition-opacity">
                  <span>Submit Meter Reading</span>
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    className="w-5 h-5 md:w-6 md:h-6 transition-transform duration-300 group-hover/btn:translate-x-1"
                  >
                    <path
                      d="M5 12h14M12 5l7 7-7 7"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </Reveal>

          {/* ── Feature Cards (CSS Grid) ── */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {features.map((feature, index) => (
              <Reveal key={index} direction="up" delay={100 + index * 100} className="w-full">
                <FeatureCard {...feature} index={index} />
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
